import React, { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import CircularProgress from '@material-ui/core/CircularProgress';

export const TenantAppPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { formData, onChange, formContext, uiSchema, schema, required } = props;
  const catalogApi = useApi(catalogApiRef);
  const [apps, setApps] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);

  const tenantFieldName = uiSchema?.['ui:options']?.tenantField || 'tenant_entity';
  const formDataObj = formContext?.formData as Record<string, unknown> | undefined;
  const tenantEntity = formDataObj?.[tenantFieldName] as string | undefined;

  useEffect(() => {
    if (!tenantEntity) {
      setApps([]);
      return;
    }

    const fetchApps = async () => {
      setLoading(true);
      try {
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'deployment',
            'spec.subcomponentOf': tenantEntity,
          },
        });
        setApps(response.items);
      } catch {
        setApps([]);
      }
      setLoading(false);
    };

    fetchApps();
  }, [tenantEntity, catalogApi]);

  const options = apps.map(app => ({
    label: app.metadata.title || app.metadata.name,
    value: `component:${app.metadata.namespace || 'default'}/${app.metadata.name}`,
  }));

  const label = schema.title || 'Deployment';
  const helperText = !tenantEntity
    ? 'Please select a tenant first'
    : apps.length === 0 && !loading
      ? 'No deployments found for this tenant'
      : uiSchema?.['ui:help'] || 'Select a deployment from this tenant';

  return (
    <FormControl
      fullWidth
      margin="normal"
      variant="outlined"
      disabled={!tenantEntity || loading}
      required={required}
    >
      <InputLabel id="tenant-app-picker-label">{label}</InputLabel>
      <Select
        labelId="tenant-app-picker-label"
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        label={label}
        endAdornment={
          loading ? (
            <CircularProgress size={20} style={{ marginRight: 24 }} />
          ) : null
        }
      >
        {options.length === 0 && !loading && tenantEntity && (
          <MenuItem value="" disabled>
            No deployments found
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
